import React, { useEffect, useState } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CRow,
  CCol,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CFormSelect,
  CFormInput,
  CModal,
  CForm,
  CFormLabel,
} from "@coreui/react";
import { useDispatch, useSelector } from "react-redux";
import { fetchBids, updateBidStatus } from "../../features/seller/sellerSlice";
import { capitalizeFirst } from "../../utils/stringUtils";
import AppLoader from "../../components/seller/AppLoader";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import AppToast from "../../components/seller/AppToast";
import { formatCurrency, formatPrice } from "../../utils/price";

import { useNavigate } from "react-router-dom";

const sampleBids = [
  {
    id: 1,
    buyer: "John Doe",
    product: "Aluminium Sheet",
    bidPrice: 3100,
    status: "Pending",
    counterOffer: 3200,
  },
  {
    id: 2,
    buyer: "Jane Smith",
    product: "Copper Wire",
    bidPrice: 4200,
    status: "Accepted",
    counterOffer: null,
  },
  {
    id: 3,
    buyer: "Amit Kumar",
    product: "Brass Rod",
    bidPrice: 2700,
    status: "Rejected",
    counterOffer: 2900,
  },
];

const validationSchema = Yup.object({
  rejectReason: Yup.string().required("Reason is required"),
});

const SellerBids = () => {
  const dispatch = useDispatch();
  const { list: bids, loading } = useSelector((state) => state.seller.bids);

  const [search, setSearch] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedBid, setSelectedBid] = useState(null);
  const [counterPrice, setCounterPrice] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedBidId, setSelectedBidId] = useState(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastType, setToastType] = useState("info");
  const [toastMessage, setToastMessage] = useState("");

  useEffect(() => {
    dispatch(fetchBids())
      .unwrap()
      .catch((error) => {
        console.log("Failed to load stock list", error);
      });
  }, [dispatch]);
  console.log("Bids:", bids);
  const filtered = bids.filter((b) =>
    b.product.toLowerCase().includes(search.toLowerCase()),
  );

  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filtered.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filtered.length / entriesPerPage);

  const handleOpenCounterModal = (bid) => {
    setSelectedBid(bid);
    setCounterPrice(bid.counterOffer || "");
    setShowModal(true);
  };

  const handleSubmitCounter = async () => {
    try {
      const res = await dispatch(
        updateBidStatus({
          id: selectedBid.id,
          data: {
            status: "countered",
            counter_price: counterPrice,
          },
        }),
      ).unwrap();
      setShowModal(false);
      dispatch(fetchBids());

      setToastType("success");
      setToastMessage(res?.message || "Counter offer submitted successfully.");
      setToastVisible(true);
    } catch (error) {
      console.error("Failed to submit counter offer:", error);
      setToastType("error");
      setToastMessage(error?.message || "Failed to submit counter offer.");
      setToastVisible(true);
    }
  };

  const handleReject = async (reason) => {
    try {
      const res = await dispatch(
        updateBidStatus({
          id: selectedBidId,
          data: { status: "rejected", rejection_reason: reason },
        }),
      ).unwrap();
      setShowRejectModal(false);
      dispatch(fetchBids());

      setToastType("success");
      setToastMessage(res?.message || "Bid rejected successfully.");
      setToastVisible(true);
    } catch (error) {
      console.error("Failed to reject bid:", error);
      setToastType("error");
      setToastMessage(error?.message || "Failed to reject bid.");
      setToastVisible(true);
    }
  };

  const handleAccept = async (bidId) => {
    try {
      const res = await dispatch(
        updateBidStatus({
          id: bidId,
          data: { status: "accepted" },
        }),
      ).unwrap();
      dispatch(fetchBids());

      setToastType("success");
      setToastMessage(res?.message || "Bid accepted successfully.");
      setToastVisible(true);
    } catch (err) {
      console.error("Failed to accept bid:", err);
      setToastType("error");
      setToastMessage(err?.message || "Failed to accept bid.");
      setToastVisible(true);
    }
  };

  // Navigation hook for row click product detail
  const navigate = useNavigate();

  const handleRowClick = (productId) => {
    navigate(`/seller/product-detail/${productId}`);
  };

  if (loading) return <AppLoader />;

  return (
    <>
      <div className="container-fluid px-3">
        <CRow className="mt-4 mb-3">
          <CCol>
            <h4 className="mb-0">Received Bids</h4>
          </CCol>
        </CRow>

        <CCard>
          <CCardHeader>
            <CRow className="mb-2">
              <CCol>
                <h6
                  className="text-muted mb-0 d-flex align-items-center"
                  style={{ height: "100%" }}
                >
                  Bids on My Products
                </h6>
              </CCol>
            </CRow>
            <hr className="my-2" />
            <CRow className="align-items-center">
              <CCol md={6}>
                Show{" "}
                <CFormSelect
                  size="sm"
                  value={entriesPerPage}
                  onChange={(e) => setEntriesPerPage(Number(e.target.value))}
                  style={{ display: "inline-block", width: "auto" }}
                  options={[
                    { label: "5", value: "5" },
                    { label: "10", value: "10" },
                    { label: "25", value: "25" },
                  ]}
                />{" "}
                entries
              </CCol>
              <CCol md={6} className="text-end">
                Search:{" "}
                <CFormInput
                  type="text"
                  size="sm"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ display: "inline-block", width: "200px" }}
                />
              </CCol>
            </CRow>
          </CCardHeader>

          <CCardBody>
            <CTable responsive bordered hover>
              <CTableHead color="light">
                <CTableRow>
                  <CTableHeaderCell>#</CTableHeaderCell>
                  <CTableHeaderCell>Bid Id</CTableHeaderCell>
                  <CTableHeaderCell>Buyer</CTableHeaderCell>
                  <CTableHeaderCell>Product</CTableHeaderCell>
                  <CTableHeaderCell>Bid Price (AUD)</CTableHeaderCell>
                  <CTableHeaderCell>Status</CTableHeaderCell>
                  <CTableHeaderCell>Counter Offer (AUD)</CTableHeaderCell>
                  <CTableHeaderCell>Reject Reason</CTableHeaderCell>
                  <CTableHeaderCell>Actions</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {currentData.map((bid, index) => (
                  <CTableRow
                    key={bid.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleRowClick(bid.product_id)}
                  >
                    <CTableDataCell>{indexOfFirst + index + 1}</CTableDataCell>
                    <CTableDataCell>{bid.unique_bid_id || "-"}</CTableDataCell>
                    <CTableDataCell>{bid.buyer || "-"}</CTableDataCell>
                    <CTableDataCell>{bid.product || "-"}</CTableDataCell>
                    <CTableDataCell>
                      {formatCurrency(bid.bid_price)}
                      {/* {bid.bid_price !== null &&
                      bid.bid_price !== undefined &&
                      bid.bid_price !== ""
                        ? `A$ ${formatPrice(bid.bid_price)}`
                        : "-"} */}
                    </CTableDataCell>
                    <CTableDataCell>
                      <span
                        className={`badge bg-${
                          bid.status === "accepted"
                            ? "success"
                            : bid.status === "rejected"
                              ? "danger"
                              : "warning"
                        }`}
                      >
                        {bid.status ? capitalizeFirst(bid.status) : "-"}
                      </span>
                    </CTableDataCell>
                    <CTableDataCell>
                      {bid.counter_offer !== null &&
                      bid.counter_offer !== undefined &&
                      bid.counter_offer !== ""
                        ? `A$ ${formatPrice(bid.counter_offer)}`
                        : "-"}
                    </CTableDataCell>
                    <CTableDataCell>
                      {bid.rejection_reason || "-"}
                    </CTableDataCell>

                    <CTableDataCell>
                      {bid.status === "pending" ? (
                        <>
                          <CButton
                            size="sm"
                            color="success"
                            className="me-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAccept(bid.id);
                            }}
                          >
                            Accept
                          </CButton>
                          <CButton
                            size="sm"
                            color="danger"
                            className="me-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedBidId(bid.id);
                              setShowRejectModal(true);
                            }}
                          >
                            Reject
                          </CButton>
                          <CButton
                            size="sm"
                            color="warning"
                            className="text-white"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenCounterModal(bid);
                            }}
                          >
                            Counter
                          </CButton>
                        </>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </CTableDataCell>
                  </CTableRow>
                ))}
                {currentData.length === 0 && (
                  <CTableRow>
                    <CTableDataCell
                      colSpan="8"
                      className="text-center text-muted"
                    >
                      No bids found.
                    </CTableDataCell>
                  </CTableRow>
                )}
              </CTableBody>
            </CTable>

            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                Showing {indexOfFirst + 1} to{" "}
                {Math.min(indexOfLast, filtered.length)} of {filtered.length}{" "}
                entries
              </div>
              <div className="d-flex align-items-center">
                <CButton
                  size="sm"
                  color="light"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className="me-2"
                >
                  Prev
                </CButton>

                {[...Array(totalPages)].map((_, i) => (
                  <CButton
                    key={i}
                    size="sm"
                    color={currentPage === i + 1 ? "primary" : "light"}
                    onClick={() => setCurrentPage(i + 1)}
                    className="me-2"
                  >
                    {i + 1}
                  </CButton>
                ))}

                <CButton
                  size="sm"
                  color="light"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </CButton>
              </div>
            </div>
          </CCardBody>
        </CCard>

        <CModal
          visible={showModal}
          onClose={() => setShowModal(false)}
          alignment="center"
        >
          <CCard className="m-0">
            <CCardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Counter Offer</h5>
                <CButton
                  color="light"
                  className="p-0"
                  onClick={() => setShowModal(false)}
                >
                  <span aria-hidden="true">&times;</span>
                </CButton>
              </div>

              <CForm>
                <CFormLabel htmlFor="counterPrice" className="fw-semibold">
                  Counter Price ($)
                </CFormLabel>
                <CFormInput
                  id="counterPrice"
                  type="number"
                  value={counterPrice}
                  onChange={(e) => setCounterPrice(e.target.value)}
                  placeholder="Enter your counter offer"
                  min={0}
                />

                <div className="d-flex justify-content-end mt-4">
                  <CButton
                    color="primary"
                    className="me-2"
                    onClick={handleSubmitCounter}
                  >
                    Submit
                  </CButton>
                  <CButton
                    color="secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Close
                  </CButton>
                </div>
              </CForm>
            </CCardBody>
          </CCard>
        </CModal>

        <CModal
          visible={showRejectModal}
          onClose={() => setShowRejectModal(false)}
          alignment="center"
        >
          <CCard className="m-0">
            <CCardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Reject Bid</h5>
                <CButton
                  color="light"
                  className="p-0"
                  onClick={() => setShowRejectModal(false)}
                >
                  <span aria-hidden="true">&times;</span>
                </CButton>
              </div>
              <Formik
                initialValues={{ rejectReason: "" }}
                validationSchema={validationSchema}
                onSubmit={(values, { setSubmitting }) => {
                  handleReject(values.rejectReason);
                  setSubmitting(false);
                }}
              >
                {({
                  values,
                  setFieldValue,
                  handleBlur,
                  touched,
                  errors,
                  isSubmitting,
                }) => (
                  <Form>
                    <CFormLabel htmlFor="rejectReason" className="fw-semibold">
                      Reason for Rejection
                    </CFormLabel>
                    <CFormInput
                      id="rejectReason"
                      name="rejectReason"
                      value={values.rejectReason}
                      onChange={(e) =>
                        setFieldValue("rejectReason", e.target.value)
                      }
                      onBlur={handleBlur}
                      placeholder="Enter reason"
                    />
                    {touched.rejectReason && errors.rejectReason && (
                      <div className="text-danger">{errors.rejectReason}</div>
                    )}
                    <div className="d-flex justify-content-end mt-4">
                      <CButton
                        color="danger"
                        className="me-2"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        Reject
                      </CButton>
                      <CButton
                        color="secondary"
                        onClick={() => setShowRejectModal(false)}
                      >
                        Cancel
                      </CButton>
                    </div>
                  </Form>
                )}
              </Formik>
            </CCardBody>
          </CCard>
        </CModal>
      </div>
      <AppToast
        visible={toastVisible}
        setVisible={setToastVisible}
        type={toastType}
        message={toastMessage}
      />
    </>
  );
};

export default SellerBids;
